import { useLayoutEffect, useRef, useState } from 'react';
import { Group, TextArea as AriaTextArea, TextField as AriaTextField } from 'react-aria-components';
import { useT } from '../../../i18n/hooks';
import { FM } from './messages';
import { FieldFoot, FieldLabel, cx, fieldClass } from './parts';
import type { TextAreaProps } from './types';
import './styles';

/**
 * Многострочное поле с автовысотой: растёт с текстом от `minRows` до
 * `maxRows`, дальше — прокрутка внутри. Высота считается по `scrollHeight`,
 * а не по числу переводов строки: длинная строка тоже переносится.
 */
export function TextArea({ label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, size = 'md', className, value, onChange, placeholder, minRows = 3, maxRows = 8, maxLength }: TextAreaProps) {
  const t = useT();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [clamped, setClamped] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      const style = getComputedStyle(el);
      const font = parseFloat(style.fontSize) || 13;
      const line = parseFloat(style.lineHeight) || font * 1.45;
      const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      el.style.height = 'auto';
      const content = el.scrollHeight;
      const min = minRows * line + padding;
      const max = maxRows * line + padding;
      el.style.height = `${Math.min(Math.max(content, min), max)}px`;
      setClamped(content > max);
    };
    fit();
    // Ширина меняется — меняются переносы: пересчитать.
    if (typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth !== width) {
        width = el.clientWidth;
        fit();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, minRows, maxRows]);

  return (
    <AriaTextField
      className={fieldClass(size, className)}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      validationBehavior="aria"
      aria-label={label ? undefined : ariaLabel}
    >
      <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
      <Group className={cx('ac-trigger', 'fm-input', 'fm-area')} isDisabled={isDisabled} isInvalid={isInvalid} data-clamped={clamped ? '' : undefined}>
        <AriaTextArea ref={ref} rows={minRows} placeholder={placeholder} />
      </Group>
      <FieldFoot description={description} errorMessage={errorMessage} isInvalid={isInvalid} />
      {maxLength ? (
        <span className="fm-counter" data-over={value.length >= maxLength ? '' : undefined} aria-hidden="true">
          {t(FM.counter, { count: value.length, max: maxLength })}
        </span>
      ) : null}
    </AriaTextField>
  );
}
